import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, XCircle, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface QueueItem {
  id: string;
  entity_type: string;
  entity_id: string;
  status: string;
  priority: number;
  attempts: number;
  error_message: string | null;
  created_at: string;
}

interface ContentQueueProcessorProps {
  siteId: string;
  onComplete?: () => void;
}

export function ContentQueueProcessor({ siteId, onComplete }: ContentQueueProcessorProps) {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch queue items
  const fetchQueue = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("content_generation_queue")
      .select("*")
      .eq("site_id", siteId)
      .order("priority", { ascending: false })
      .order("created_at", { ascending: true });

    if (!fetchError && data) {
      setQueueItems(data);
    } else if (fetchError) {
      setError(fetchError.message);
    }
  }, [siteId]);

  // Initial fetch and realtime subscription
  useEffect(() => {
    fetchQueue();

    // Subscribe to realtime updates with direct state updates
    const channel = supabase
      .channel(`queue-${siteId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "content_generation_queue",
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          setQueueItems(prev => [...prev, payload.new as QueueItem]);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "content_generation_queue",
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          setQueueItems(prev => 
            prev.map(item => 
              item.id === (payload.new as QueueItem).id 
                ? payload.new as QueueItem 
                : item
            )
          );
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "content_generation_queue",
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          setQueueItems(prev => 
            prev.filter(item => item.id !== (payload.old as QueueItem).id)
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId, fetchQueue]);

  // Call onComplete when all items are done
  useEffect(() => {
    const pending = queueItems.filter(item => item.status === "pending").length;
    const processing = queueItems.filter(item => item.status === "processing").length;
    
    if (queueItems.length > 0 && pending === 0 && processing === 0) {
      onComplete?.();
    }
  }, [queueItems, onComplete]);

  // Calculate stats
  const completed = queueItems.filter(item => item.status === "completed").length;
  const failed = queueItems.filter(item => item.status === "failed").length;
  const pending = queueItems.filter(item => item.status === "pending").length;
  const processing = queueItems.filter(item => item.status === "processing").length;
  const total = queueItems.length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Separate towns and areas
  const townItems = queueItems.filter(item => item.entity_type === "town");
  const areaItems = queueItems.filter(item => item.entity_type === "area");
  const completedTowns = townItems.filter(item => item.status === "completed").length;
  const completedAreas = areaItems.filter(item => item.status === "completed").length;

  if (total === 0) {
    return null;
  }

  const isComplete = pending === 0 && processing === 0;
  const isProcessing = processing > 0;

  return (
    <div className={`rounded-lg border p-4 mb-6 ${
      isComplete 
        ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800" 
        : error 
        ? "bg-destructive/10 border-destructive/20"
        : "bg-primary/5 border-primary/20"
    }`}>
      <div className="flex items-start gap-3">
        {isComplete ? (
          <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
        ) : isProcessing ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary mt-0.5" />
        ) : failed > 0 && pending === 0 ? (
          <XCircle className="h-5 w-5 text-destructive mt-0.5" />
        ) : (
          <Zap className="h-5 w-5 text-amber-500 mt-0.5" />
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold text-sm">
              {isComplete 
                ? "Content Generation Complete!" 
                : isProcessing
                ? "Generating Content..."
                : pending > 0
                ? "Background Processing Active"
                : "Content Generation"}
            </h3>
            
            {!isComplete && pending > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Zap className="h-3 w-3" />
                Auto-processing
              </span>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            {isComplete 
              ? `🎉 Generated content for ${completed} items${failed > 0 ? ` (${failed} failed)` : ""}`
              : pending > 0
              ? `Processing automatically in background. You can leave this page.`
              : `${completed} of ${total} items completed`}
          </p>

          {!isComplete && (
            <div className="mt-3 space-y-2">
              <Progress value={progress} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  Towns: {completedTowns}/{townItems.length} • 
                  Areas: {completedAreas}/{areaItems.length}
                </span>
                <span>{progress}%</span>
              </div>
            </div>
          )}

          {error && (
            <p className="mt-2 text-sm text-destructive">
              Error: {error}
            </p>
          )}

          {failed > 0 && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
              {failed} item(s) failed. You can retry them later.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
